import { TranslatePipe, TranslateService } from "@ngx-translate/core";

import { ChangeDetectionStrategy, Component, computed, inject, output } from "@angular/core";
import { RouterModule } from "@angular/router";

import { AliasEditorComponent } from "../../../../components/common/alias-editor/alias-editor.component";
import { SubjectOrGroup } from "../../../../components/common/line-chart/line-chart-subject-dropdown";
import { TrafficLineChart } from "../../../../components/dashboard/traffic-line-chart/traffic-line-chart.component";
import {
  TrafficSubject,
  makeAllTrafficOption,
} from "../../../../components/dashboard/traffic-line-chart/traffic-line-chart.helpers";
import { SelfserviceToggle } from "../../../../components/selfservice/selfservice-toggle/selfservice-toggle";
import { ServiceId } from "../../../../components/subscription-detail/service-id/service-id";
import { SubscriptionDetailHeader } from "../../../../components/subscription-detail/subscription-detail-header/subscription-detail-header";
import { ThirdPartyServiceIdsRowContentComponent } from "../../../../components/subscription-detail/third-party-service-ids-row-content/third-party-service-ids-row-content";
import { EnabledPipe } from "../../../../helpers/enabledpipe/enabledpipe";
import { SelfserviceDialogType } from "../../../../helpers/self-service/models/types";
import { ServiceSpeedPipe } from "../../../../helpers/service-speed-pipe/service-speed-pipe";
import { SubscriptionDatePipe } from "../../../../helpers/subscription-datepipe/subscription-datepipe";
import { AuthService } from "../../../../services/authservice";
import { LightPathBothSubscriptionDetails } from "../../types";
import { SubscriptionViewBase } from "../subscription-view-base";
import { LightpathConfigComponent } from "./components/lightpath-config/lightpath-config";
import { LightpathShortCutMenu } from "./components/lightpath-shortcut-menu/lightpath-shortcut-menu";

@Component({
  selector: "lightpath-subscription-view",
  templateUrl: "lightpath-subscription-view.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AliasEditorComponent,
    EnabledPipe,
    LightpathConfigComponent,
    LightpathShortCutMenu,
    RouterModule,
    SelfserviceToggle,
    ServiceId,
    ServiceSpeedPipe,
    SubscriptionDatePipe,
    SubscriptionDetailHeader,
    ThirdPartyServiceIdsRowContentComponent,
    TrafficLineChart,
    TranslatePipe,
  ],
})
export class LightpathSubscriptionView extends SubscriptionViewBase<LightPathBothSubscriptionDetails> {
  selfServiceOpen = output<SelfserviceDialogType>();

  protected auth = inject(AuthService);

  protected speedPolicer = computed(() => this.subscription().speedPolicer);
  private circuits = computed(() => this.subscription().circuits);

  protected remotePortshutdown = computed(() => this.circuits()[0].remotePortShutdown);
  protected allPortsAreUntagged = computed(() =>
    this.circuits().every((circuit) => circuit.endpoints.every((endpoint) => endpoint.port?.portMode === "Untagged")),
  );
  protected allPortsAreNotAggregated = computed(() =>
    this.circuits().every((circuit) => circuit.endpoints.every((endpoint) => endpoint.port?.product.tag !== "AGGSP")),
  );

  private translate = inject(TranslateService);
  protected chartSubjects = computed(() => buildLightpathChartSubjects(this.subscription(), this.translate));
}

export function buildLightpathChartSubjects(
  subscription: LightPathBothSubscriptionDetails,
  translate: TranslateService,
): SubjectOrGroup<TrafficSubject>[] {
  return [
    makeAllTrafficOption(subscription.subscriptionId, translate),
    ...subscription.circuits.map((circuit, index) => ({
      name: `${index + 1}. ${circuit.endpoints[0]?.port?.address?.city ?? ""} - ${circuit.endpoints[1]?.port?.address?.city ?? ""}`,
      subjects: circuit.endpoints.map((endpoint) => ({
        name: endpoint.port?.description ?? "",
        subscriptionId: subscription.subscriptionId,
        subscriptionInstanceIds: [endpoint.subscriptionInstanceId],
      })),
    })),
  ];
}
